/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/CardPlacementsSection.tsx
 * FILE_ID: UI-KANBAN-DRAWER-PLACEMENTS-001
 * PURPOSE: "Boards & lanes" drawer section — manage the card's ARC-10
 *          placements (extra live presences) from the drawer: list each
 *          presence with its board › lane name, remove one, or add a new
 *          presence on any board/lane in the org.
 * WHY: Arc A (email-card-boards unification) — placements existed but were
 *      right-click/Alt-drag only; the drawer could not see or manage them,
 *      so operators fell back to mirror-copies that drift. This is the
 *      always-reachable management surface.
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────────┐   ┌───────────────────┐
 *   │ card.lane_    │ → │ add/remove via        │ → │ invalidate boards │
 *   │ placements    │   │ api.*CardPlacement    │   │ + onChanged(card) │
 *   └───────────────┘   └──────────────────────┘   └───────────────────┘
 * DEPENDENCIES:
 *   - api.addCardPlacement / removeCardPlacement: the ARC-10 endpoints
 *   - api.listBoards / listLanes: the add-presence picker
 * RELATED:
 *   - context-menu/sections/AllocateToLaneSubmenu.tsx: same verbs, menu surface
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Loader2, MapPin, Plus, X } from 'lucide-react'
import type { KanbanApi } from '../../../api'
import type { Board, Card, Lane } from '../../../types'
import { buildPlacementRows } from './cardPlacementRows'

export interface CardPlacementsSectionProps {
  card: Card
  api: KanbanApi
  boardId?: string
  /** Current board's lanes — names the home presence without a fetch. */
  lanes: Lane[]
  /** Navigate to another board (the host deep-link seam — wired from
   *  KanbanBoardView's `onSwitchBoard`). When omitted, the per-row
   *  "Open board" affordance renders DISABLED rather than dead. */
  onOpenBoard?: (boardId: string) => void
}

const TAG_TONE_CLASS: Record<'archived' | 'deleted' | 'noaccess', string> = {
  archived:
    'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  deleted: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  noaccess:
    'bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
}

export function CardPlacementsSection({ card, api, boardId, lanes, onOpenBoard }: CardPlacementsSectionProps) {
  const qc = useQueryClient()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [boards, setBoards] = useState<Board[] | null>(null)
  const [pickedBoard, setPickedBoard] = useState<Board | null>(null)
  const [pickedBoardLanes, setPickedBoardLanes] = useState<Lane[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!api.addCardPlacement || !api.removeCardPlacement) return null

  const placements = card.lane_placements ?? []
  const rows = buildPlacementRows(card, boardId, lanes)

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['kanban', 'cards'] })
    void qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id] })
  }

  const openAdd = async () => {
    setAdding(true)
    setError(null)
    if (boards === null) {
      setLoading(true)
      try {
        // P34 — scope to the card's org so multi-org operators cannot
        // pick a foreign board (BE rejects cross-org; UI must not list it).
        const orgId = card.organisation
        const listed = await api.listBoards(
          orgId != null ? { organisationId: orgId, writable: true } : undefined,
        )
        setBoards(
          orgId != null
            ? listed.filter((b) => b.organisation === orgId)
            : listed,
        )
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load boards')
      } finally {
        setLoading(false)
      }
    }
  }

  const pickBoard = async (b: Board) => {
    setPickedBoard(b)
    setPickedBoardLanes(null)
    setLoading(true)
    setError(null)
    try {
      const loaded = await api.listLanes(b.id)
      const placed = new Set(placements.map((p) => p.lane))
      setPickedBoardLanes(
        loaded.filter((l) => !l.archived && !placed.has(l.id) && l.id !== card.lane),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load lanes')
    } finally {
      setLoading(false)
    }
  }

  const addToLane = async (lane: Lane) => {
    setBusyId(lane.id)
    setError(null)
    try {
      await api.addCardPlacement!(card.id, { lane_id: lane.id })
      invalidate()
      setAdding(false)
      setPickedBoard(null)
      setPickedBoardLanes(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add presence failed')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (placementId: string) => {
    setBusyId(placementId)
    setError(null)
    try {
      await api.removeCardPlacement!(card.id, placementId)
      invalidate()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove presence failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-1.5" data-testid="card-placements-section">
      <p className="text-[10px] leading-snug text-neutral-400">
        Same card, live on every board listed — edits reflect everywhere
        (unlike "Copy to board", which makes an independent copy).
      </p>
      {rows.map((row) => (
        <div
          key={row.key}
          className="flex items-center justify-between gap-2 rounded-md bg-neutral-50 px-2 py-1.5 text-xs dark:bg-neutral-900/50"
          data-testid={
            row.isHome ? 'card-placement-row-home' : `card-placement-row-${row.placementId}`
          }
        >
          <span className="flex min-w-0 flex-1 items-center gap-1.5 text-neutral-600 dark:text-neutral-300">
            {!row.isHome && <span aria-hidden>⧉</span>}
            <span className="min-w-0 truncate">{row.label}</span>
            {row.tag && (
              <span
                className={[
                  'shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wide',
                  TAG_TONE_CLASS[row.tag.tone],
                ].join(' ')}
                data-testid={`card-placement-tag-${row.key}`}
              >
                {row.tag.label}
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {row.isCurrent ? (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400"
                data-testid={`card-placement-here-${row.key}`}
              >
                <MapPin className="h-3 w-3" />
                You are here
              </span>
            ) : (
              <button
                type="button"
                disabled={!row.navigable || !onOpenBoard}
                onClick={() => {
                  if (row.navigable && row.boardId && onOpenBoard) {
                    onOpenBoard(row.boardId)
                  }
                }}
                title={
                  row.navigable
                    ? onOpenBoard
                      ? 'Open this board'
                      : "Board navigation isn't available here"
                    : row.disabledReason ?? 'This board can\'t be opened'
                }
                aria-label={`Open board ${row.label}`}
                className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-neutral-400 disabled:hover:bg-transparent dark:text-blue-400 dark:hover:bg-blue-950/40 dark:disabled:text-neutral-500"
                data-testid={`card-placement-open-${row.key}`}
              >
                <ExternalLink className="h-3 w-3" />
                Open
              </button>
            )}
            {!row.isHome && row.placementId && (
              <button
                type="button"
                aria-label={`Remove presence in ${row.label}`}
                disabled={busyId === row.placementId}
                onClick={() => void remove(row.placementId!)}
                className="shrink-0 rounded p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-red-600 disabled:opacity-50 dark:hover:bg-neutral-800"
                data-testid={`card-placement-remove-${row.placementId}`}
              >
                {busyId === row.placementId ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <X className="h-3 w-3" />
                )}
              </button>
            )}
          </span>
        </div>
      ))}
      {!adding ? (
        <button
          type="button"
          onClick={() => void openAdd()}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40"
          data-testid="card-placement-add"
        >
          <Plus className="h-3 w-3" />
          Also show on another board / lane…
        </button>
      ) : (
        <div className="rounded-md border border-neutral-200 p-2 dark:border-neutral-800">
          {loading && (
            <p className="text-xs text-neutral-400">
              <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
              Loading…
            </p>
          )}
          {!pickedBoard &&
            (boards ?? []).map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => void pickBoard(b)}
                className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-neutral-100 dark:hover:bg-neutral-900"
                data-testid={`card-placement-pick-board-${b.id}`}
              >
                {b.name}
              </button>
            ))}
          {pickedBoard && (
            <>
              <button
                type="button"
                onClick={() => {
                  setPickedBoard(null)
                  setPickedBoardLanes(null)
                }}
                className="mb-1 text-[10px] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              >
                ← boards
              </button>
              {(pickedBoardLanes ?? []).map((l) => (
                <button
                  key={l.id}
                  type="button"
                  disabled={busyId === l.id}
                  onClick={() => void addToLane(l)}
                  className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-neutral-100 disabled:opacity-50 dark:hover:bg-neutral-900"
                  data-testid={`card-placement-pick-lane-${l.id}`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {busyId === l.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    {pickedBoard.name} › {l.name}
                  </span>
                </button>
              ))}
              {pickedBoardLanes !== null && pickedBoardLanes.length === 0 && (
                <p className="px-2 py-1 text-[10px] text-neutral-400">
                  No available lanes on this board.
                </p>
              )}
            </>
          )}
          <button
            type="button"
            onClick={() => {
              setAdding(false)
              setPickedBoard(null)
              setPickedBoardLanes(null)
              setError(null)
            }}
            className="mt-1 text-[10px] text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          >
            Cancel
          </button>
        </div>
      )}
      {error && <p className="text-[10px] text-red-500">{error}</p>}
    </div>
  )
}
